import { useEffect, useMemo, type FC } from 'react'
import { Button, Spin } from 'antd'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import type { IconBaseProps } from 'react-icons'
import {
  MdArrowBack as MdArrowBackRaw,
  MdMusicNote as MdMusicNoteRaw,
} from 'react-icons/md'
import { useTranslation } from 'react-i18next'

import './DeliveredPlaylists.scss'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import type { Order } from '../../utils/redux/ordersSlice'
import { attemptGetUserOrders } from '../../utils/redux/ordersSlice'
import { apiBaseURL } from '../../utils/api'
import {
  extractOrderPlaylists,
  formatDate,
  getOrderDetailChips,
  getOrderPreferenceDetails,
  getOrderStatusLabel,
  orderHasDeliveredPlaylists,
  toLocaleFromLang,
} from './orderSummary'

type LocationState = {
  order?: Order
}

const MdArrowBack = MdArrowBackRaw as unknown as FC<IconBaseProps>
const MdMusicNote = MdMusicNoteRaw as unknown as FC<IconBaseProps>

export default function DeliveredPlaylists() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation('orders')
  const location = useLocation()
  const { orderId = '' } = useParams<{ orderId: string }>()

  const { allOrders, listLoading } = useAppSelector((s: any) => s.orders)
  const state = (location.state || {}) as LocationState

  const orderFromStore = useMemo(() => {
    const arr = Array.isArray(allOrders) ? (allOrders as Order[]) : []
    return arr.find((item) => String(item._id) === String(orderId))
  }, [allOrders, orderId])

  const order = state.order || orderFromStore

  useEffect(() => {
    if (!state.order && !orderFromStore && orderId) {
      void dispatch(attemptGetUserOrders())
    }
  }, [dispatch, orderFromStore, orderId, state.order])

  const locale = useMemo(
    () => toLocaleFromLang(i18n.resolvedLanguage || i18n.language || 'en'),
    [i18n.language, i18n.resolvedLanguage],
  )

  const playlists = useMemo(() => {
    if (!order) return []
    return extractOrderPlaylists(order)
  }, [order])

  const detailChips = useMemo(() => {
    if (!order) return []
    return getOrderDetailChips(order, t)
  }, [order, t])

  const preferenceDetails = useMemo(() => {
    if (!order) return []
    return getOrderPreferenceDetails(order, t)
  }, [order, t])

  if (listLoading && !order) {
    return (
      <div className="deliveredPlaylistsPage deliveredPlaylistsPage--loading">
        <Spin size="large" />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="deliveredPlaylistsPage">
        <div className="deliveredPlaylistsInner">
          <button
            type="button"
            className="dpBackBtn"
            onClick={() => navigate('/orders')}
            aria-label={t('a11y.back', { defaultValue: 'Back' })}
          >
            <MdArrowBack size={22} />
            <span>{t('a11y.back', { defaultValue: 'Back' })}</span>
          </button>

          <div className="dpEmptyState">
            <h2>{t('deliveredPlaylists.notFoundTitle', { defaultValue: 'Order not found' })}</h2>
            <p>
              {t('deliveredPlaylists.notFoundText', {
                defaultValue:
                  'We could not find this order. Please return to your orders list and open it again.',
              })}
            </p>
            <Button type="primary" onClick={() => navigate('/orders')}>
              {t('deliveredPlaylists.backToOrders', { defaultValue: 'Back to orders' })}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const hasDelivered = orderHasDeliveredPlaylists(order)

  return (
    <div className="deliveredPlaylistsPage">
      <div className="deliveredPlaylistsInner">
        <div className="dpHeader">
          <button
            type="button"
            className="dpBackBtn"
            onClick={() => navigate('/orders')}
            aria-label={t('a11y.back', { defaultValue: 'Back' })}
          >
            <MdArrowBack size={22} />
            <span>{t('a11y.back', { defaultValue: 'Back' })}</span>
          </button>
          <h1 className="dpTitle">{formatDate(order.createdAt, locale)}</h1>
        </div>

        <section className="dpSummaryCard" aria-label={t('deliveredPlaylists.summaryAria', { defaultValue: 'Order summary' })}>
          <div className="dpSummaryTop">
            <div>
              <p className="dpLabel">
                {t('deliveredPlaylists.summaryTitle', { defaultValue: 'Order summary' })}
              </p>
              <h2 className="dpStatusTitle">{getOrderStatusLabel(order, t)}</h2>
            </div>
            <span className={`dpStatusBadge ${hasDelivered ? 'isReady' : 'isProcessing'}`}>
              {hasDelivered
                ? t('deliveredPlaylists.ready', { defaultValue: 'Ready' })
                : t('status.processing', { defaultValue: 'Processing your order' })}
            </span>
          </div>

          <div className="dpMetaBlock">
            <p className="dpMetaMain">
              {[order.businessName, order.activityType].filter(Boolean).join(' • ')}
            </p>
            {order.address ? <p className="dpMetaSub">{order.address}</p> : null}
          </div>

          {detailChips.length > 0 ? (
            <div className="dpChipsRow">
              {detailChips.map((chip) => (
                <span key={chip} className="dpChip">
                  {chip}
                </span>
              ))}
            </div>
          ) : null}

          {preferenceDetails.length > 0 ? (
            <div className="dpPreferences">
              {preferenceDetails.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ) : null}

          {order.extraClarifications ? (
            <div className="dpNotesBlock">
              <p className="dpLabel">{t('deliveredPlaylists.notes', { defaultValue: 'Notes' })}</p>
              <p>{order.extraClarifications}</p>
            </div>
          ) : null}

          {order.linkTracksExamples ? (
            <div className="dpNotesBlock">
              <p className="dpLabel">
                {t('deliveredPlaylists.trackExamples', { defaultValue: 'Track examples' })}
              </p>
              <p>{order.linkTracksExamples}</p>
            </div>
          ) : null}
        </section>

        {playlists.length > 0 ? (
          <>
            <h3 className="dpSectionTitle">
              {t('deliveredPlaylists.title', { defaultValue: 'Delivered playlists' })}
            </h3>

            <div className="dpGrid">
              {playlists.map((playlist) => {
                const imageUrl = playlist.image
                  ? `${apiBaseURL}/uploaded/playlists/${playlist.image}`
                  : '/random.jpg'

                return (
                  <button
                    key={playlist._id}
                    type="button"
                    className="dpPlaylistCard"
                    onClick={() =>
                      navigate(`/playlist/${playlist._id}`, {
                        state: {
                          fromDeliveredOrder: true,
                          orderId: order._id,
                          playlistId: playlist._id,
                        },
                      })
                    }
                  >
                    <img src={imageUrl} alt={playlist.title} loading="lazy" />
                    <div className="dpPlaylistOverlay" />
                    <div className="dpPlaylistBody">
                      <MdMusicNote size={18} />
                      <span>{playlist.title}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        ) : (
          <div className="dpEmptyState">
            <MdMusicNote size={26} />
            <h3>
              {t('deliveredPlaylists.emptyTitle', {
                defaultValue: 'We are still preparing your playlists',
              })}
            </h3>
            <p>
              {t('deliveredPlaylists.emptyText', {
                defaultValue:
                  'You can review order details here while the playlist is processing.',
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
